/**
 * Post 임베드 위젯 — 특정 뉴스레터를 카드/리스트/피처드로 표시
 * 공통 스타일: shared/style.scss (.ejs-widget)
 *
 * SOURCE:
 *   "specific" — 특정 뉴스레터 검색·선택 (Note + Page 공통)
 *   "latest"   — 최신 발행 뉴스레터 자동 표시 (Page 전용)
 *
 * LAYOUT:
 *   "list"     — 리스트 (Note + Page 공통)
 *   "card"     — 카드 (Note + Page 공통)
 *   "featured" — 피처드 (Page 전용)
 *
 * Page 전용 기능 제어:
 *   config.pageMode (editor_core.js의 includePageWidgets) 플래그로 결정.
 *   - pageMode=true  → latest 소스 + featured 레이아웃 사용 가능
 *   - pageMode=false → source는 항상 "specific", featured는 "list"로 강제
 *   서버에서도 latest는 웹(Page)에서만 렌더링, 이메일에서는 무시.
 *   (post_embed.rb 참고)
 *
 * VIEW: 카드/리스트/피처드 미리보기 (클릭 → EDIT)
 * EDIT: 검색 UI (외부 클릭 → VIEW 복귀, 선택 → VIEW)
 */
import { stopPropagationOnEditorJsControlKeys } from '@/editorjs/utils';
import api from '@/utils/api';

export default class PostEmbedTool {
  static get toolbox() {
    return {
      title: "뉴스레터 임베드",
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M3 19a9 9 0 0 1 9 0a9 9 0 0 1 9 0" /><path d="M3 6a9 9 0 0 1 9 0a9 9 0 0 1 9 0" /><path d="M3 6l0 13" /><path d="M12 6l0 13" /><path d="M21 6l0 13" /></svg>'
    };
  }

  static get isReadOnlySupported() { return true; }

  constructor({ data, config, api, readOnly }) {
    this.api = api;
    this.readOnly = readOnly;
    this.config = config || {};
    // pageMode: Page 에디터에서만 true (editor_core.js의 includePageWidgets)
    // Note 에디터에서는 false → latest/featured 사용 불가
    this.pageMode = this.config.pageMode || false;
    const rawLayout = data?.layout || "list";
    const rawSource = data?.source || "specific";
    this.data = Object.assign({
      // Note 모드: featured → list로 강제, source → specific으로 강제
      layout: (!this.pageMode && rawLayout === "featured") ? "list" : rawLayout,
      source: this.pageMode ? rawSource : "specific",
      post_ext_id: null,
      title: null,
      subtitle: null,
      published_at: null,
      thumbnail: null,
    }, data || {});
    this.wrapper = null;
    this.editing = false;
    this.searchTimeout = null;
  }

  render() {
    this.wrapper = document.createElement("div");

    if (this.data.source === "latest") {
      if (this.readOnly) {
        // Server renders this — show nothing or minimal placeholder
        this.wrapper.className = "ejs-widget";
        this.wrapper.style.cssText = "padding: 0;";
      } else {
        this._renderLatestPlaceholder();
      }
    } else if (this.data.post_ext_id) {
      this._renderView();
    } else {
      this._renderEdit();
    }

    if (!this.readOnly) {
      // 카드뷰 클릭 → 편집 모드
      this.wrapper.addEventListener("click", (e) => {
        if (this.editing) return;
        if (e.target.closest(".ejs-widget__controls")) return;
        this._renderEdit();
      });

      // 외부 클릭 → 뷰 복귀
      this._handleClickOutside = (e) => {
        if (!this.editing) return;
        if (this.wrapper.contains(e.target)) return;
        if (this.data.source === "latest") {
          this._renderLatestPlaceholder();
        } else if (this.data.post_ext_id) {
          this._renderView();
        }
      };
      document.addEventListener("click", this._handleClickOutside, true);
    }

    return this.wrapper;
  }

  destroy() {
    if (this._handleClickOutside) document.removeEventListener("click", this._handleClickOutside, true);
  }

  // === VIEW 모드 (카드/리스트/피처드 미리보기) ===

  _renderView() {
    this.editing = false;
    this.wrapper.style.cursor = this.readOnly ? "" : "pointer";
    const layout = this.data.layout || "list";
    if (layout === "featured") this._renderFeaturedPreview();
    else if (layout === "card") this._renderCardPreview();
    else this._renderListPreview();
  }

  _renderCardPreview() {
    const { title, subtitle, published_at, thumbnail } = this.data;
    const thumbHtml = thumbnail
      ? `<div style="width: 100%; padding-top: 52.5%; background: url('${this._escape(thumbnail)}') center/cover no-repeat;"></div>`
      : '';

    this.wrapper.className = "ejs-widget";
    this.wrapper.style.cssText = "padding: 0; overflow: hidden;" + (this.readOnly ? "" : " cursor: pointer;");
    this.wrapper.innerHTML = `
      ${thumbHtml}
      <div style="padding: 14px 18px;">
        <p style="font-size: 16px; font-weight: 600; margin: 0 0 4px 0; line-height: 1.4;">${this._escape(title)}</p>
        ${subtitle ? `<p style="font-size: 13px; margin: 0 0 6px 0; line-height: 1.4;" class="ejs-widget__hint">${this._escape(subtitle)}</p>` : ''}
        <div>${this._footerLineHtml(published_at)}</div>
        ${!this.readOnly ? '<p class="ejs-widget__hint" style="margin: 8px 0 0 0;">클릭하여 편집</p>' : ''}
      </div>
    `;
  }

  _renderFeaturedPreview() {
    const { title, subtitle, published_at, thumbnail } = this.data;
    const thumbHtml = thumbnail
      ? `<div style="flex-shrink: 0; width: 280px; align-self: stretch; background: url('${this._escape(thumbnail)}') center/cover no-repeat;"></div>`
      : '';

    this.wrapper.className = "ejs-widget";
    this.wrapper.style.cssText = "padding: 0; overflow: hidden;" + (this.readOnly ? "" : " cursor: pointer;");
    this.wrapper.innerHTML = `
      <div style="display: flex; align-items: stretch;">
        ${thumbHtml}
        <div style="flex: 1; min-width: 200px; display: flex; flex-direction: column; padding: 20px 24px;">
          <p style="font-size: 22px; font-weight: 700; margin: 0 0 8px 0; line-height: 1.35;">${this._escape(title)}</p>
          ${subtitle ? `<p style="font-size: 15px; margin: 0 0 8px 0; line-height: 1.5;" class="ejs-widget__hint">${this._escape(subtitle)}</p>` : ''}
          <div style="margin-top: auto; padding-top: 8px;">
            <div style="margin: 0 0 6px 0;">${this._footerLineHtml(published_at, "13px")}</div>
            <span style="font-size: 15px; font-weight: 500;">읽어보기 →</span>
          </div>
        </div>
      </div>
      ${!this.readOnly ? '<p class="ejs-widget__hint" style="margin: 8px 0 0 0; text-align: center;">클릭하여 편집</p>' : ''}
    `;
  }

  _renderListPreview() {
    const { title, subtitle, published_at, thumbnail } = this.data;
    const thumbHtml = thumbnail
      ? `<div style="flex-shrink: 0; width: 220px; align-self: stretch; background: url('${this._escape(thumbnail)}') center/cover no-repeat;"></div>`
      : '';

    this.wrapper.className = "ejs-widget";
    this.wrapper.style.cssText = "padding: 0; overflow: hidden;" + (this.readOnly ? "" : " cursor: pointer;");
    this.wrapper.innerHTML = `
      <div style="display: flex; align-items: stretch;">
        <div style="flex: 1; min-width: 0; padding: 16px 20px;">
          <p style="font-size: 15px; font-weight: 500; margin: 0 0 4px 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${this._escape(title)}</p>
          ${subtitle ? `<p style="font-size: 13px; margin: 0 0 6px 0; line-height: 1.4; color: #94a3b8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${this._escape(subtitle)}</p>` : ''}
          <div>${this._footerLineHtml(published_at, "13px")}</div>
        </div>
        ${thumbHtml}
      </div>
      ${!this.readOnly ? '<p class="ejs-widget__hint" style="margin: 8px 0 0 0; text-align: center;">클릭하여 편집</p>' : ''}
    `;
  }

  // === LATEST 모드 플레이스홀더 (에디터 전용) ===

  _renderLatestPlaceholder() {
    this.editing = false;
    this.wrapper.className = "ejs-widget";
    this.wrapper.style.cssText = "text-align: center; cursor: pointer;";
    this.wrapper.innerHTML = `
      <div style="padding: 8px 0;">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block; margin-bottom: 8px; opacity: 0.5;"><path d="M3 19a9 9 0 0 1 9 0a9 9 0 0 1 9 0" /><path d="M3 6a9 9 0 0 1 9 0a9 9 0 0 1 9 0" /><path d="M3 6l0 13" /><path d="M12 6l0 13" /><path d="M21 6l0 13" /></svg>
        <p style="font-size: 14px; margin: 0 0 4px 0;">최신 발행 뉴스레터가 자동으로 표시됩니다</p>
        <p class="ejs-widget__hint" style="margin: 0;">클릭하여 편집</p>
      </div>
    `;
  }

  // === EDIT 모드 (검색) ===

  _renderEdit() {
    this.editing = true;
    const layout = this.data.layout || "list";
    const source = this.data.source || "specific";
    this.wrapper.className = "ejs-widget ejs-widget--editing";
    this.wrapper.style.cssText = "";
    // 소스 토글: pageMode에서만 표시 (latest는 Page 전용)
    const sourceHtml = this.pageMode ? `
        <div>
          <label class="ejs-widget__label">소스</label>
          <div style="display: flex; gap: 4px;">
            <button type="button" data-source="specific" class="ejs-widget__btn ${source === 'specific' ? 'ejs-widget__btn--active' : ''}">특정 뉴스레터</button>
            <button type="button" data-source="latest" class="ejs-widget__btn ${source === 'latest' ? 'ejs-widget__btn--active' : ''}">최신 뉴스레터</button>
          </div>
        </div>` : '';

    // 피처드 버튼: pageMode에서만 표시 (featured는 Page 전용)
    const featuredBtn = this.pageMode
      ? `<button type="button" data-layout="featured" class="ejs-widget__btn ${layout === 'featured' ? 'ejs-widget__btn--active' : ''}">피처드</button>`
      : '';

    this.wrapper.innerHTML = `
      <div style="display: flex; flex-direction: column; gap: 12px;">
        ${sourceHtml}
        <div>
          <label class="ejs-widget__label">레이아웃</label>
          <div style="display: flex; gap: 4px;">
            <button type="button" data-layout="list" class="ejs-widget__btn ${layout === 'list' ? 'ejs-widget__btn--active' : ''}">리스트</button>
            <button type="button" data-layout="card" class="ejs-widget__btn ${layout === 'card' ? 'ejs-widget__btn--active' : ''}">카드</button>
            ${featuredBtn}
          </div>
        </div>
        <div class="post-embed-source-content"></div>
      </div>
    `;

    const contentArea = this.wrapper.querySelector(".post-embed-source-content");

    // Source toggle
    this.wrapper.querySelectorAll("[data-source]").forEach(btn => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.data.source = btn.dataset.source;
        if (this.data.source === "latest") {
          this.data.post_ext_id = null;
          this.data.title = null;
          this.data.subtitle = null;
          this.data.published_at = null;
          this.data.thumbnail = null;
        }
        this._renderEdit();
      });
    });

    // Layout toggle
    this.wrapper.querySelectorAll("[data-layout]").forEach(btn => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.data.layout = btn.dataset.layout;
        this.wrapper.querySelectorAll("[data-layout]").forEach(b => b.classList.remove("ejs-widget__btn--active"));
        btn.classList.add("ejs-widget__btn--active");
      });
    });

    if (source === "latest") {
      this._renderLatestEditContent(contentArea);
    } else {
      this._renderSpecificEditContent(contentArea);
    }
  }

  _renderLatestEditContent(container) {
    container.innerHTML = `
      <p style="font-size: 13px; margin: 0; text-align: center; padding: 8px 0;" class="ejs-widget__hint">최신 발행 뉴스레터가 자동으로 표시됩니다</p>
    `;
  }

  _renderSpecificEditContent(container) {
    container.innerHTML = `
      <div>
        <label class="ejs-widget__label">뉴스레터 검색</label>
        <input type="text" placeholder="제목 또는 부제목으로 검색" class="ejs-widget__input ejs-widget__input--sm">
        <div class="post-embed-results" style="margin-top: 8px; max-height: 240px; overflow-y: auto;"></div>
      </div>
    `;

    const input = container.querySelector("input");
    const results = container.querySelector(".post-embed-results");

    input.addEventListener("input", () => {
      clearTimeout(this.searchTimeout);
      const q = input.value.trim();
      if (q.length < 1) { results.innerHTML = ""; this._loadInitial(results); return; }
      this.searchTimeout = setTimeout(async () => {
        try {
          const posts = await this._fetchPosts(q);
          this._renderResults(results, posts);
        } catch (e) {
          results.innerHTML = '<p style="font-size: 12px; text-align: center; margin: 12px 0;" class="ejs-widget__hint">검색에 실패했습니다</p>';
        }
      }, 300);
    });

    input.addEventListener("keydown", (e) => stopPropagationOnEditorJsControlKeys(e));

    this._loadInitial(results);
  }

  // === API ===

  async _fetchPosts(q = "") {
    const spaceUrl = this.config.spaceUrl || "";
    const params = q ? { q } : {};
    // 데모에는 space_url(작성자)이 없으므로 discover 인기 레터 scope 내에서 검색/목록을 제공한다.
    const endpoint = spaceUrl === "demo"
      ? `/app/notes/demo_search`
      : `/api/${spaceUrl}/posts/search`;
    const res = await api.get(endpoint, params);
    return res.data;
  }

  async _loadInitial(results) {
    try { const posts = await this._fetchPosts(); this._renderResults(results, posts); } catch (e) { /* ignore */ }
  }

  _renderResults(container, posts) {
    if (!posts || posts.length === 0) {
      container.innerHTML = '<p style="font-size: 12px; text-align: center; margin: 12px 0;" class="ejs-widget__hint">검색 결과가 없습니다</p>';
      return;
    }

    container.innerHTML = posts.map(post => {
      // 날짜 · 부제 · 멤버십 · 유료 콘텐츠 — 비어있는 항목은 구분자까지 빠진다.
      const meta = [];
      if (post.published_at) meta.push(this._escape(post.published_at));
      if (post.subtitle) meta.push(this._escape(post.subtitle));
      meta.push(...this._badgeSpans(post, "11px"));

      return `
      <div class="ejs-widget__result-item">
        <p style="font-size: 13px; font-weight: 500; margin: 0;">${this._escape(post.title)}</p>
        <p style="font-size: 11px; margin: 2px 0 0 0;" class="ejs-widget__hint">${meta.join(" · ")}</p>
      </div>
    `;
    }).join("");

    container.querySelectorAll(".ejs-widget__result-item").forEach((item, idx) => {
      item.addEventListener("click", (e) => {
        e.stopPropagation();
        const post = posts[idx];
        this.data = {
          post_ext_id: post.ext_id, title: post.title,
          subtitle: post.subtitle || "", published_at: post.published_at || "",
          thumbnail: post.thumbnail || null, layout: this.data.layout || "list",
          source: "specific",
          category: post.category || null, membership: post.membership || false,
          product: post.product || false,
        };
        this._renderView();
      });
    });
  }

  // === Save ===

  save() {
    const base = { layout: this.data.layout || "list", source: this.data.source || "specific" };
    if (this.data.source === "latest") return base;
    return {
      ...base,
      post_ext_id: this.data.post_ext_id,
      title: this.data.title,
      subtitle: this.data.subtitle,
      published_at: this.data.published_at,
      thumbnail: this.data.thumbnail,
      category: this.data.category || null,
      membership: this.data.membership || false,
      product: this.data.product || false,
    };
  }

  validate(savedData) {
    return savedData.source === "latest" || !!savedData.post_ext_id;
  }

  _escape(str) { const d = document.createElement("div"); d.textContent = str || ""; return d.innerHTML; }

  // 글 선택 목록에서도 멤버십/유료 콘텐츠 여부를 보여준다.
  // 고르는 시점에 이 정보가 없으면, 삽입한 뒤에야 배지를 보고 다시 지우게 된다.
  // 표기는 footer(_footerLineHtml) 와 같은 문구·색을 쓴다.
  _badgeSpans(post, fontSize = "11px") {
    const badges = [];
    if (post.membership) badges.push(`<span style="font-size: ${fontSize}; font-weight: 600; color: #15803d;">멤버십</span>`);
    if (post.product) badges.push(`<span style="font-size: ${fontSize}; font-weight: 600; color: #475569;">유료 콘텐츠</span>`);
    return badges;
  }

  // 통합 footer: 날짜 · 카테고리 · 멤버십 · 유료 콘텐츠
  // ⚠️ 항목 순서/표시 변경 시 아래 동등 함수들도 함께 수정해야 함:
  //   - app/helpers/editor_js/blocks/post_card/web.rb#web_footer_line (웹)
  //   - app/helpers/editor_js/blocks/post_card/email.rb#email_footer_line (이메일)
  _footerLineHtml(date, fontSize = "12px") {
    const { category, membership, product } = this.data;
    const dot = '<span style="color: #d1d5db; margin: 0 4px;">·</span>';
    const parts = [];

    parts.push(`<span style="font-size: ${fontSize}; color: #94a3b8;">${this._escape(date)}</span>`);
    if (category) parts.push(`<span style="font-size: ${fontSize}; color: #94a3b8;">${this._escape(category)}</span>`);
    if (membership) parts.push(`<span style="font-size: ${fontSize}; font-weight: 500; color: #15803d;">멤버십</span>`);
    if (product) parts.push(`<span style="font-size: ${fontSize}; font-weight: 600; color: #475569;">유료 콘텐츠</span>`);

    return `<div style="display: flex; flex-wrap: wrap; align-items: center; gap: 2px;">${parts.join(dot)}</div>`;
  }
}
